/**
 * Placement maths for the offer graphic collage.
 *
 * Every number for 1-6 and 9 is read from the design's variations board
 * (Figma 14326:80204); 7 and 8 are not drawn there and are derived below.
 * Board numbers are percentages of that board's 414x388 frame: `x`/`size`
 * against its width, `y` against its height. The collage box keeps the same
 * ratio, so the percentages transfer unchanged at any scale. The image sits
 * inside its ring at 79% of the ring's diameter (the board's 127/159 and
 * 105/135).
 */

import { CollageArcShape } from "./collageArcs";

export type CollagePlacement = {
  /** Centre of the dotted ring. */
  x: number;
  y: number;
  /** Ring diameter, as a percentage of the box width. */
  size: number;
};

/**
 * Star, sparkle and the two arc flourishes, placed by centre.
 *
 * `size` and `height` are the footprint the board gives the layer, as
 * percentages of the box's width and height. The artwork is fitted inside that
 * footprint rather than stretched to it.
 */
export type CollageDecoration = {
  x: number;
  y: number;
  size: number;
  height: number;
};

/**
 * One arc flourish: which of the board's artworks, and the layer bounds the
 * board gives it — centre plus size, as percentages of the board's width and
 * height. The turn lives in the artwork, so these are read straight off Figma.
 */
export type CollageArc = {
  shape: CollageArcShape;
  x: number;
  y: number;
  width: number;
  height: number;
};

type CollageDecorations = {
  /** Figma "Stroke 27" — the eight-point star, top-left. */
  star: CollageDecoration;
  /** Figma "icon 2" / "Group 50" — the burst, bottom-right. */
  sparkle: CollageDecoration;
  /**
   * The board turns and resizes these per count, and splits each pair into
   * loose vectors for one and two images, so they are a list rather than a
   * fixed leading/trailing slot.
   */
  arcs: CollageArc[];
};

type CollageArrangement = {
  items: CollagePlacement[];
  decorations: CollageDecorations;
};

/** The design tops out at nine; extras are dropped, not shrunk. */
export const MAX_COLLAGE_ITEMS = 9;

// Decoration sets, shared by the counts that draw them identically.
const DECO_PAIR: CollageDecorations = {
  star: { x: 10.51, y: 24.35, size: 4.59, height: 4.9 },
  sparkle: { x: 88.76, y: 74.58, size: 7.03, height: 7.5 },
  arcs: [
    { shape: "start", x: 11.59, y: 50.43, width: 4.06, height: 14.79 },
    { shape: "startEcho", x: 10.68, y: 55.13, width: 3.79, height: 6.1 },
    { shape: "end", x: 79.66, y: 37.48, width: 11.99, height: 11.81 },
    { shape: "endEcho", x: 76.27, y: 31.89, width: 5.02, height: 2.97 },
  ],
};

const DECO_TRIPLE: CollageDecorations = {
  star: { x: 11.13, y: 17.72, size: 6.33, height: 6.75 },
  sparkle: { x: 85.65, y: 72.49, size: 7.78, height: 8.3 },
  arcs: [
    { shape: "startPair", x: 22.93, y: 60.3, width: 18.1, height: 20.25 },
    { shape: "endPair", x: 83.76, y: 21.16, width: 16.54, height: 19.12 },
  ],
};

const DECO_QUAD: CollageDecorations = {
  star: { x: 11.07, y: 13.47, size: 6.21, height: 6.62 },
  sparkle: { x: 88.21, y: 83.0, size: 7.63, height: 8.14 },
  arcs: [
    { shape: "startPair", x: 19.2, y: 87.22, width: 17.73, height: 19.84 },
    { shape: "endPair", x: 82.21, y: 16.84, width: 16.21, height: 18.73 },
  ],
};

const DECO_SIX: CollageDecorations = {
  star: { x: 11.07, y: 16.07, size: 6.21, height: 6.62 },
  sparkle: { x: 88.21, y: 85.59, size: 7.63, height: 8.14 },
  arcs: [
    {
      shape: "startPairTurned",
      x: 18.29,
      y: 86.77,
      width: 19.72,
      height: 16.79,
    },
    { shape: "endPairTurned", x: 80.99, y: 26.57, width: 20.48, height: 22.83 },
  ],
};

const DECO_NINE: CollageDecorations = {
  star: { x: 11.07, y: 17.1, size: 6.21, height: 6.62 },
  sparkle: { x: 88.21, y: 86.62, size: 7.63, height: 8.14 },
  arcs: [
    {
      shape: "startPairTurned",
      x: 25.53,
      y: 90.37,
      width: 19.72,
      height: 16.79,
    },
    { shape: "endPairTurned", x: 74.01, y: 23.22, width: 20.48, height: 22.83 },
  ],
};

const RING_NINE = 19.43;

/** The board's nine-up grid: three even rows of three. */
function nineUpGrid(): CollagePlacement[] {
  const columns = [30.2, 50.1, 69.9];
  const rows = [30.2, 50.9, 71.7];

  return rows.flatMap((y) => columns.map((x) => ({ x, y, size: RING_NINE })));
}

const ARRANGEMENTS: Record<number, CollageArrangement> = {
  // The board's one-product frame keeps the two-up template and blanks the
  // right slot; a lone circle reads better centred.
  1: {
    items: [{ x: 50, y: 46.7, size: 38.51 }],
    decorations: DECO_PAIR,
  },
  2: {
    items: [
      { x: 32.5, y: 46.0, size: 38.51 },
      { x: 67.7, y: 53.5, size: 38.51 },
    ],
    decorations: DECO_PAIR,
  },
  3: {
    items: [
      { x: 35.6, y: 37.1, size: 32.58 },
      { x: 69.0, y: 37.1, size: 32.58 },
      { x: 53.1, y: 71.0, size: 32.58 },
    ],
    decorations: DECO_TRIPLE,
  },
  4: {
    items: [
      { x: 35.0, y: 32.5, size: 31.91 },
      { x: 67.7, y: 32.5, size: 31.91 },
      { x: 35.0, y: 66.4, size: 31.91 },
      { x: 67.7, y: 66.4, size: 31.91 },
    ],
    decorations: DECO_QUAD,
  },
  // The fifth sits over the other four, drawn last.
  5: {
    items: [
      { x: 35.0, y: 32.5, size: 31.91 },
      { x: 67.7, y: 32.5, size: 31.91 },
      { x: 35.0, y: 66.4, size: 31.91 },
      { x: 67.7, y: 66.4, size: 31.91 },
      { x: 50.0, y: 50.0, size: 31.91 },
    ],
    decorations: DECO_QUAD,
  },
  6: {
    items: [
      { x: 23.8, y: 37.1, size: 25.54 },
      { x: 49.8, y: 37.1, size: 25.54 },
      { x: 75.9, y: 37.1, size: 25.54 },
      { x: 23.8, y: 64.3, size: 25.54 },
      { x: 49.8, y: 64.3, size: 25.54 },
      { x: 75.9, y: 64.3, size: 25.54 },
    ],
    decorations: DECO_SIX,
  },
  /*
   * 7 and 8 are not drawn on the board. They follow the five-up's motif
   * instead — a ring with one more sitting over its middle — so seven is a
   * hexagon plus centre and eight a heptagon plus centre. Ring circles just
   * touch, as the five-up's square does, and each ring is scaled to the
   * nine-up's footprint so the whole family keeps one silhouette.
   */
  7: {
    items: [
      { x: 50.06, y: 30.21, size: 19.43 },
      { x: 66.88, y: 40.57, size: 19.43 },
      { x: 66.88, y: 61.3, size: 19.43 },
      { x: 50.06, y: 71.66, size: 19.43 },
      { x: 33.23, y: 61.3, size: 19.43 },
      { x: 33.23, y: 40.57, size: 19.43 },
      { x: 50.06, y: 50.93, size: 19.43 },
    ],
    decorations: DECO_NINE,
  },
  8: {
    items: [
      { x: 50.06, y: 29.68, size: 18.19 },
      { x: 66.44, y: 38.1, size: 18.19 },
      { x: 70.49, y: 57.02, size: 18.19 },
      { x: 59.15, y: 72.19, size: 18.19 },
      { x: 40.96, y: 72.19, size: 18.19 },
      { x: 29.62, y: 57.02, size: 18.19 },
      { x: 33.67, y: 38.1, size: 18.19 },
      { x: 50.06, y: 52.04, size: 18.19 },
    ],
    decorations: DECO_NINE,
  },
  9: { items: nineUpGrid(), decorations: DECO_NINE },
};

/** The design board every arrangement is laid out on (Figma 14326:80204). */
export const DESIGN_WIDTH = 414;

export const DESIGN_HEIGHT = 388;

/** Where an arrangement actually draws, in board pixels. */
type CollageBand = {
  /** Distance from the board's top edge to the first ink. */
  top: number;
  /** Height of the drawn band. */
  height: number;
};

/*
 * `y` is a percentage of the board's height, but a ring's `size` is a
 * percentage of its *width* — the slot is square — while a decoration carries
 * its own `height` percentage. Both are centred on their `y`.
 */
const itemBounds = ({ y, size }: CollagePlacement): [number, number] => {
  const radius = ((size / 100) * DESIGN_WIDTH) / 2;
  const centre = (y / 100) * DESIGN_HEIGHT;

  return [centre - radius, centre + radius];
};

const boxBounds = ({
  y,
  height,
}: {
  y: number;
  height: number;
}): [number, number] => {
  const half = ((height / 100) * DESIGN_HEIGHT) / 2;
  const centre = (y / 100) * DESIGN_HEIGHT;

  return [centre - half, centre + half];
};

/**
 * The band an arrangement occupies, flourishes included.
 *
 * The board is 388 tall for every count, but nothing fills it: one and two
 * images reach barely half of it, which left the collage padded with empty
 * space wherever a caller sized it by width. Measuring the drawing instead
 * lets each count carry its own aspect ratio.
 *
 * Derived from the same placements that position the rings, so the box and
 * its contents cannot drift apart. Flourishes count: `.collage` does not clip,
 * so hugging the rings alone would spill the arcs onto whatever sits beside it.
 */
export function collageBand({
  items,
  decorations,
}: CollageArrangement): CollageBand {
  const spans: [number, number][] = [
    ...items.map(itemBounds),
    boxBounds(decorations.star),
    boxBounds(decorations.sparkle),
    ...decorations.arcs.map(boxBounds),
  ];

  const top = Math.min(...spans.map(([start]) => start));
  const bottom = Math.max(...spans.map(([, end]) => end));

  return { top, height: bottom - top };
}

/** A disc to clear out of a ring, in percentages of that ring's own box. */
type CollageCutout = {
  x: number;
  y: number;
  /** Radius, as a percentage of the ring's box — which is square. */
  radius: number;
};

/*
 * Rings that merely touch are not overlaps. The board's five-up and the
 * polygons are built so neighbours meet exactly, which lands a sub-pixel apart
 * once the percentages are resolved; only a real overlap earns a hole.
 */
const TOUCH_TOLERANCE = 1;

const ringGeometry = ({ x, y, size }: CollagePlacement) => ({
  cx: (x / 100) * DESIGN_WIDTH,
  cy: (y / 100) * DESIGN_HEIGHT,
  radius: ((size / 100) * DESIGN_WIDTH) / 2,
});

/**
 * Where each ring has to be cleared so the ring drawn over it shows the
 * surface behind, not the photograph underneath.
 *
 * The board fills that band with a colour, but the collage sits on a white
 * popup, a tinted card and a tier-themed one, so the band has to be an actual
 * hole. Nothing composites a hole across siblings in CSS — no blend mode
 * erases to the backdrop — so the ring underneath is masked instead.
 *
 * Only a later ring can cut an earlier one: they are painted in order, so the
 * last one drawn is the one on top.
 */
export function collageCutouts(items: CollagePlacement[]): CollageCutout[][] {
  const rings = items.map(ringGeometry);

  return rings.map((ring, index) =>
    rings.slice(index + 1).reduce<CollageCutout[]>((cutouts, over) => {
      const gap =
        Math.hypot(ring.cx - over.cx, ring.cy - over.cy) -
        (ring.radius + over.radius);

      if (gap > -TOUCH_TOLERANCE) {
        return cutouts;
      }

      // The ring's own box, which the mask's percentages resolve against.
      const size = ring.radius * 2;

      return [
        ...cutouts,
        {
          x: ((over.cx - (ring.cx - ring.radius)) / size) * 100,
          y: ((over.cy - (ring.cy - ring.radius)) / size) * 100,
          radius: (over.radius / size) * 100,
        },
      ];
    }, [])
  );
}

/** Arrangement for `count` graphics, capped at {@link MAX_COLLAGE_ITEMS}. */
export function collageArrangement(
  count: number
): CollageArrangement | undefined {
  const capped = Math.min(Math.max(Math.floor(count), 0), MAX_COLLAGE_ITEMS);

  return ARRANGEMENTS[capped];
}
